import React from "react";
import { Flex, Text } from "styles/utility-components";

import Checkbox from "components/ui/checkbox/Checkbox";
import Divider from "components/ui/divider/Divider";
import { Stage } from "./Stage";
import { GroupedStageOptions, StageOption } from "./types";

type Props = {
  stageGroups: GroupedStageOptions[];
  checkedStageIds: string[];
  onChange: (values: string[]) => void;
};

export const WorfklowStageCheckboxList = React.forwardRef<HTMLDivElement, Props>(
  ({ stageGroups, checkedStageIds, onChange }, ref) => {
    const handleSelect = (stageId: string) => {
      if (checkedStageIds.includes(stageId)) {
        onChange(checkedStageIds.filter((stage) => stage !== stageId));
      } else {
        onChange([...checkedStageIds, stageId]);
      }
    };

    const handleGroupSelectAll = (allOptionsInGroup: StageOption[]) => {
      const allStageIdsInGroup = allOptionsInGroup.map((stageOption) => stageOption.value);
      const stageIdsMissingInCurrentSelection = allStageIdsInGroup.filter(
        (stageId) => !checkedStageIds.includes(stageId),
      );

      if (stageIdsMissingInCurrentSelection.length === 0) {
        onChange(checkedStageIds.filter((stageId) => !allStageIdsInGroup.includes(stageId)));
      } else {
        onChange([...checkedStageIds, ...stageIdsMissingInCurrentSelection]);
      }
    };

    return (
      <Flex column gap="24px" ref={ref}>
        {stageGroups.map((group, index) => (
          <Flex key={group.value} column gap="16px">
            {index !== 0 && <Divider margin="0" />}
            <Text color="sysTextBlack">{group.label}</Text>
            <Flex column gap="8px">
              {group.options.length > 1 && (
                <Flex alignItems="center" gap="8px">
                  <Checkbox
                    checked={group.options.every((option) =>
                      checkedStageIds.includes(option.value),
                    )}
                    onCheckedChange={() => handleGroupSelectAll(group.options)}
                    aria-label={`Toggle all ${group.label} stages`}
                  />
                  <Text onClick={() => handleGroupSelectAll(group.options)}>Select All</Text>
                </Flex>
              )}
              {group.options.map((option) => (
                <Flex key={option.value} alignItems="center" gap="8px">
                  <Checkbox
                    checked={checkedStageIds.includes(option.value)}
                    onCheckedChange={() => handleSelect(option.value)}
                    aria-label={`Toggle ${option.label} stage`}
                  />
                  <Stage {...option} />
                </Flex>
              ))}
            </Flex>
          </Flex>
        ))}
      </Flex>
    );
  },
);

WorfklowStageCheckboxList.displayName = "WorfklowStageCheckboxList";
